import { useState } from 'react';
import { useSubmit } from 'react-router-dom';
import { FormKey } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/return-summary/return-summary-action';
import { useReturnSummaryLoaderData } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/return-summary/return-summary-loader';
import { returnSummarySearchParams } from '~/app/[:store]/order/[:idFromPlatform]/[:claimType]/return-summary/return-summary-search-params';
import Badge from '~/components/badge';
import RadioGroup from '~/components/form-controls/radio-group';
import { ExchangeMethod, exchangeMethodLabel } from '~/utils/constants';
import ReturnSummaryDisclosure from './return-summary-disclosure';

function ExchangeMethodRadioGroup() {
  const {
    exchangeMethod: {
      offered: offeredExchangeMethods,
      selected: selectedExchangeMethod,
    },
  } = useReturnSummaryLoaderData();

  const canInstantExchange = offeredExchangeMethods.some(
    (method) => method.type === ExchangeMethod.instant,
  );

  const submit = useSubmit();

  const [exchangeMethod, setExchangeMethod] = useState<ExchangeMethod | null>(
    selectedExchangeMethod,
  );

  const handleChange = (method: ExchangeMethod) => {
    setExchangeMethod(method);
    submit(
      { [returnSummarySearchParams.exchangeMethod]: method },
      { method: 'GET', replace: true, preventScrollReset: true },
    );
  };

  return canInstantExchange ?
      <RadioGroup label="Exchange Options" showLabel={false}>
        <div className="flex flex-col gap-2">
          {offeredExchangeMethods.map((method) => (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label
              key={method.type}
              className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-gray-50"
            >
              <input
                type="radio"
                name={FormKey.exchangeMethod}
                value={method.type}
                checked={exchangeMethod === method.type}
                onChange={() => handleChange(method.type)}
                required
                className="mt-1"
              />
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2 font-medium text-gray-900">
                  {method.label}
                  {method.type === ExchangeMethod.instant && (
                    <Badge variant="success">Fastest</Badge>
                  )}
                </div>
                <p className="text-gray-500">{method.description}</p>
              </div>
            </label>
          ))}
        </div>
      </RadioGroup>
    : <input
        data-testid="default-exchange-method"
        type="hidden"
        name={FormKey.exchangeMethod}
        value={ExchangeMethod.standard}
      />;
}

export default function ExchangeSelection() {
  const loaderData = useReturnSummaryLoaderData();

  const {
    exchangeMethod: {
      selected: selectedExchangeMethod,
      offered: offeredMethods,
    },
  } = loaderData;

  // standard exchange / no exchange required
  if (offeredMethods.length <= 1)
    return (
      <input
        data-testid="default-exchange-method"
        type="hidden"
        name={FormKey.exchangeMethod}
        value={ExchangeMethod.standard}
      />
    );

  return (
    <>
      <ReturnSummaryDisclosure
        label="Selections"
        value={
          selectedExchangeMethod ?
            exchangeMethodLabel[selectedExchangeMethod]
          : '1 Remaining'
        }
        defaultOpen
      >
        <ExchangeMethodRadioGroup />
      </ReturnSummaryDisclosure>
      <hr />
    </>
  );
}
